import { format } from "date-fns";
import { Flag } from "lucide-react";
import { Link } from "react-router-dom";
import { SignInStatusDot } from "@/components/signin-status-dot";
import { Badge } from "@/components/ui/badge";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ManagerReportRecentSignIn } from "@/types/reports";

type RecentSignInsTableProps = {
  rows: ManagerReportRecentSignIn[];
  isLoading?: boolean;
  emptyLabel?: string;
  limit?: number;
};

function formatTimestamp(value: string | null) {
  if (!value) return "Still signed in";
  return format(new Date(value), "PPpp");
}

export function RecentSignInsTable({
  rows,
  isLoading,
  emptyLabel = "No sign-ins captured for these filters.",
  limit,
}: RecentSignInsTableProps) {
  const displayRows = typeof limit === "number" ? rows.slice(0, limit) : rows;

  if (isLoading) {
    return <p>Loading site activity…</p>;
  }

  if (displayRows?.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No sign-ins captured</EmptyTitle>
          <EmptyDescription>{emptyLabel}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="min-w-[15px] sm:min-w-[20px]"></TableHead>
          <TableHead className="min-w-[60px] sm:min-w-[120px]">
            Worker
          </TableHead>
          <TableHead className="min-w-[50px] sm:min-w-[100px]">
            Job Title
          </TableHead>
          <TableHead className="min-w-[50px] sm:min-w-[100px]">Site</TableHead>
          <TableHead className="min-w-[70px] sm:min-w-[130px]">
            Signed in
          </TableHead>
          <TableHead className="min-w-[70px] sm:min-w-[130px]">
            Signed out
          </TableHead>
          <TableHead className="min-w-[60px] sm:min-w-[100px]">
            Status
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {displayRows.map((entry) => (
          <TableRow key={entry.signInId}>
            <TableCell>
              <SignInStatusDot online={!entry.signOutTime} />
            </TableCell>
            <TableCell className="capitalize">
              <Link to={`/signins/${entry.signInId}`} className="underline">
                {entry.workerName ?? "—"}
              </Link>
            </TableCell>
            <TableCell className="capitalize">
              {entry.jobTitle ?? "—"}
            </TableCell>
            <TableCell className="capitalize">{entry.siteName}</TableCell>
            <TableCell className="text-muted-foreground">
              {formatTimestamp(entry.signInTime)}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {entry.signOutTime
                ? formatTimestamp(entry.signOutTime)
                : "Still signed in"}
            </TableCell>
            <TableCell>
              {entry.flagged ? (
                <Badge
                  variant="destructive"
                  className="gap-1 whitespace-nowrap"
                >
                  <Flag className="h-3 w-3" aria-hidden="true" />
                  Flagged
                </Badge>
              ) : (
                <Badge variant="ghost" className="gap-1 whitespace-nowrap">
                  Normal
                </Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
